// Watch Page
// Responsive video player, episode sidebar, server selection

import * as React from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Box,
  Container,
  Grid,
  Typography,
  IconButton,
  Stack,
  useTheme,
  useMediaQuery,
  CircularProgress,
  Tooltip,
  Chip,
  Drawer,
  Divider,
  Button,
} from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';
import {
  PlayArrow,
  SkipNext,
  Settings,
  Subtitles,
  Fullscreen,
  ChevronLeft,
  Menu as MenuIcon,
  ArrowBack,
  FiberManualRecord,
  AutoAwesomeMotion,
} from '@mui/icons-material';
import {
  fetchAnimeDetail,
  fetchEpisodes,
  fetchEmbedServers,
  Episode,
} from '../../hooks/useApi';
import { addContinueWatching } from '../../hooks/recent';
import { Button as UIButton } from '../../components/ui/Button';

export default function Watch() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [searchParams, setSearchParams] = useSearchParams();

  const numId = parseInt(id || '0', 10);
  const epNum = parseInt(searchParams.get('ep') || '1', 10);
  const title = searchParams.get('title') || '';

  const { data } = useQuery({
    queryKey: ['anime', numId],
    queryFn: () => fetchAnimeDetail(numId),
    enabled: !!numId,
  });

  const { data: episodes = [], isLoading: loadingEpisodes } = useQuery({
    queryKey: ['episodes', numId],
    queryFn: () => fetchEpisodes(numId, numId),
    enabled: !!numId,
  });

  const { data: servers = [], isLoading: loadingServers } = useQuery({
    queryKey: ['servers', numId, epNum, title],
    queryFn: () => fetchEmbedServers(numId, epNum, title, numId),
    enabled: !!numId,
  });

  const [activeServer, setActiveServer] = React.useState(0);
  const [showControls, setShowControls] = React.useState(true);
  const [showSettings, setShowSettings] = React.useState(false);
  const [showSubtitles, setShowSubtitles] = React.useState(true);
  const [autoNext, setAutoNext] = React.useState(true);
  const [sidebarOpen, setSidebarOpen] = React.useState(false);
  const [skippedIntro, setSkippedIntro] = React.useState(false);

  const anime = data?.anime;
  const currentEp = episodes.find((e: Episode) => e.number === epNum) || episodes[0];

  React.useEffect(() => {
    setActiveServer(0);
    setSkippedIntro(false);
    window.scrollTo({ top: 0 });
  }, [epNum]);

  React.useEffect(() => {
    if (anime) {
      addContinueWatching({
        id: anime.id,
        title: anime.title,
        poster: anime.poster,
        type: anime.type,
        episode: epNum,
      });
    }
  }, [anime, epNum]);

  const goToEpisode = (num: number) => {
    const params = new URLSearchParams(searchParams);
    params.set('ep', String(num));
    if (title) params.set('title', title);
    setSearchParams(params);
  };

  const nextEpisode = () => {
    const next = epNum + 1;
    if (next <= (anime?.episodes || episodes.length || 0)) {
      goToEpisode(next);
    }
  };

  const currentUrl = servers[activeServer]?.url || '';

  return (
    <Box sx={{ width: '100%', minHeight: '100vh', bgcolor: 'var(--anime-token-background-paper)' }}>
        <Container maxWidth="xl" sx={{ py: { xs: 1, md: 2 } }}>
          <Grid container spacing={2}>
            {/* Player + Info */}
            <Grid item xs={12} md={8} lg={9}>
              {/* Top Bar */}
              <Stack
                direction="row"
                alignItems="center"
                spacing={1}
                sx={{ mb: 1.5 }}
              >
                <IconButton onClick={() => navigate(-1)} aria-label="Back">
                  <ArrowBack />
                </IconButton>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography
                    variant="subtitle1"
                    fontWeight={700}
                    noWrap
                    sx={{ fontSize: { xs: '0.95rem', md: '1.1rem' } }}
                  >
                    {anime?.title}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Episode {epNum}{currentEp?.title ? ` · ${currentEp.title}` : ''}
                  </Typography>
                </Box>
                {isMobile && (
                  <IconButton onClick={() => setSidebarOpen(true)} aria-label="Episodes">
                    <MenuIcon />
                  </IconButton>
                )}
              </Stack>

              {/* Video Player */}
              <Box
                onMouseEnter={() => setShowControls(true)}
                onMouseLeave={() => setShowControls(false)}
                sx={{
                  position: 'relative',
                  width: '100%',
                  aspectRatio: '16 / 9',
                  borderRadius: { xs: 2, md: 3 },
                  overflow: 'hidden',
                  backgroundColor: '#000',
                  border: '1px solid var(--anime-token-border-default)',
                  boxShadow: 'var(--anime-token-shadow-lg)',
                }}
              >
                {loadingServers ? (
                  <Box
                    sx={{
                      position: 'absolute',
                      inset: 0,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 2,
                      color: 'white',
                    }}
                  >
                    <CircularProgress sx={{ color: 'var(--anime-token-brand-primary-main)' }} />
                    <Typography variant="body2" color="rgba(255,255,255,0.7)">
                      Loading player…
                    </Typography>
                  </Box>
                ) : currentUrl ? (
                  <iframe
                    key={currentUrl}
                    src={currentUrl}
                    title={`${anime?.title} - Episode ${epNum}`}
                    allowFullScreen
                    style={{ width: '100%', height: '100%', border: 0 }}
                  />
                ) : (
                  <Box
                    sx={{
                      position: 'absolute',
                      inset: 0,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 1,
                      color: 'rgba(255,255,255,0.8)',
                      textAlign: 'center',
                      px: 2,
                    }}
                  >
                    <PlayArrow sx={{ fontSize: 56, opacity: 0.5 }} />
                    <Typography variant="body1">No stream available for this episode</Typography>
                    <Typography variant="caption" color="rgba(255,255,255,0.5)">
                      Try another server below
                    </Typography>
                  </Box>
                )}

                {/* Skip Intro (mock overlay) */}
                <AnimatePresence>
                  {currentUrl && !skippedIntro && !isMobile && showControls && (
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 20 }}
                      style={{ position: 'absolute', bottom: 20, right: 20 }}
                    >
                      <UIButton
                        variant="soft"
                        size="small"
                        onClick={() => setSkippedIntro(true)}
                        startIcon={<SkipNext fontSize="small" />}
                      >
                        Skip Intro
                      </UIButton>
                    </motion.div>
                  )}
                </AnimatePresence>
              </Box>

              {/* Player Controls Bar */}
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                flexWrap="wrap"
                useFlexGap
                sx={{ mt: 1.5 }}
              >
                <UIButton
                  variant="gradient"
                  size="small"
                  startIcon={<SkipNext fontSize="small" />}
                  onClick={nextEpisode}
                  disabled={epNum >= (anime?.episodes || episodes.length || 0)}
                >
                  Next Episode
                </UIButton>

                <Tooltip title={autoNext ? 'Auto-next: ON' : 'Auto-next: OFF'}>
                  <IconButton
                    onClick={() => setAutoNext(v => !v)}
                    color={autoNext ? 'primary' : 'default'}
                    aria-label="Auto next"
                  >
                    <AutoAwesomeMotion />
                  </IconButton>
                </Tooltip>

                <Tooltip title={showSubtitles ? 'Subtitles: ON' : 'Subtitles: OFF'}>
                  <IconButton
                    onClick={() => setShowSubtitles(v => !v)}
                    color={showSubtitles ? 'primary' : 'default'}
                    aria-label="Subtitles"
                  >
                    <Subtitles />
                  </IconButton>
                </Tooltip>

                <UIButton
                  variant={showSettings ? 'soft' : 'outlined'}
                  size="small"
                  startIcon={<Settings fontSize="small" />}
                  onClick={() => setShowSettings(v => !v)}
                >
                  Settings
                </UIButton>

                <Box sx={{ flex: 1 }} />

                <Chip
                  size="small"
                  icon={<FiberManualRecord sx={{ fontSize: 12, color: '#22C55E !important' }} />}
                  label="SUB"
                  variant="outlined"
                  sx={{ borderColor: 'var(--anime-token-border-default)' }}
                />
              </Stack>

              {/* Settings Panel */}
              <AnimatePresence>
                {showSettings && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                  >
                    <Box
                      sx={{
                        mt: 1.5,
                        p: 2,
                        borderRadius: 2,
                        backgroundColor: 'var(--anime-token-surface-default)',
                        border: '1px solid var(--anime-token-border-default)',
                      }}
                    >
                      <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5 }}>
                        Playback Settings
                      </Typography>
                      <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
                        <Box>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                            Auto-play Next
                          </Typography>
                          <Chip
                            size="small"
                            label={autoNext ? 'On' : 'Off'}
                            color={autoNext ? 'primary' : 'default'}
                            onClick={() => setAutoNext(v => !v)}
                          />
                        </Box>
                        <Box>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                            Subtitles
                          </Typography>
                          <Chip
                            size="small"
                            label={showSubtitles ? 'On' : 'Off'}
                            color={showSubtitles ? 'primary' : 'default'}
                            onClick={() => setShowSubtitles(v => !v)}
                          />
                        </Box>
                      </Stack>
                    </Box>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Server Selection */}
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                  Servers
                </Typography>
                {loadingServers ? (
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    {Array.from({ length: 3 }).map((_, i) => (
                      <CircularProgress key={i} size={20} sx={{ m: 1 }} />
                    ))}
                  </Stack>
                ) : servers.length > 0 ? (
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    {servers.map((s: any, i: number) => (
                      <Chip
                        key={i}
                        label={s.name}
                        clickable
                        color={activeServer === i ? 'primary' : 'default'}
                        variant={activeServer === i ? 'filled' : 'outlined'}
                        onClick={() => setActiveServer(i)}
                        sx={{ fontWeight: 600 }}
                      />
                    ))}
                  </Stack>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No servers found for this episode.
                  </Typography>
                )}
              </Box>

              {/* Synopsis */}
              {anime?.synopsis && (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                    About
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                    {anime.synopsis}
                  </Typography>
                </Box>
              )}
            </Grid>

            {/* Episode Sidebar (desktop) */}
            {!isMobile && (
              <Grid item md={4} lg={3}>
                <EpisodeSidebar
                  episodes={episodes}
                  loading={loadingEpisodes}
                  current={epNum}
                  onSelect={goToEpisode}
                />
              </Grid>
            )}
          </Grid>
        </Container>

        {/* Episode Sidebar (mobile drawer) */}
        <Drawer
          anchor="right"
          open={sidebarOpen}
          onClose={() => setSidebarOpen(false)}
          PaperProps={{ sx: { width: '85%', maxWidth: 380, bgcolor: 'var(--anime-token-background-paper)' } }}
        >
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ p: 2 }}>
            <Typography variant="h6" fontWeight={800}>Episodes</Typography>
            <IconButton onClick={() => setSidebarOpen(false)} aria-label="Close">
              <ChevronLeft />
            </IconButton>
          </Stack>
          <Divider />
          <Box sx={{ overflowY: 'auto' }}>
            <EpisodeSidebar
              episodes={episodes}
              loading={loadingEpisodes}
              current={epNum}
              onSelect={(n) => { goToEpisode(n); setSidebarOpen(false); }}
              scrollable
            />
          </Box>
        </Drawer>
      </Box>
  );
}

function EpisodeSidebar({
  episodes,
  loading,
  current,
  onSelect,
  scrollable,
}: {
  episodes: Episode[];
  loading: boolean;
  current: number;
  onSelect: (n: number) => void;
  scrollable?: boolean;
}) {
  const theme = useTheme();
  return (
    <Box
      sx={{
        borderRadius: 3,
        backgroundColor: 'var(--anime-token-surface-default)',
        border: '1px solid var(--anime-token-border-default)',
        overflow: 'hidden',
        ...(scrollable ? {} : { maxHeight: '75vh', overflowY: 'auto' }),
      }}
    >
      <Box sx={{ p: 2, borderBottom: '1px solid var(--anime-token-divider)' }}>
        <Typography variant="subtitle1" fontWeight={800}>
          Episodes
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {episodes.length} total
        </Typography>
      </Box>
      {loading ? (
        <Stack spacing={1} sx={{ p: 2 }}>
          {Array.from({ length: 10 }).map((_, i) => (
            <Box key={i} sx={{ height: 56, borderRadius: 2, backgroundColor: 'var(--anime-token-surface-variant)' }} />
          ))}
        </Stack>
      ) : (
        <Stack spacing={0.5} sx={{ p: 1 }}>
          {episodes.map((ep: Episode, index) => {
            const active = ep.number === current;
            return (
              <motion.div
                key={ep.id}
                initial={{ opacity: 0, x: 10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.2, delay: Math.min(index * 0.01, 0.3) }}
              >
                <Box
                  onClick={() => onSelect(ep.number)}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    p: 1.5,
                    borderRadius: 2,
                    cursor: 'pointer',
                    backgroundColor: active ? 'var(--anime-token-brand-primary-500_15)' : 'transparent',
                    border: active ? '1px solid var(--anime-token-brand-primary-main)' : '1px solid transparent',
                    transition: 'all 0.15s ease',
                    '&:hover': {
                      backgroundColor: active ? 'var(--anime-token-brand-primary-500_15)' : 'var(--anime-token-surface-variant)',
                    },
                  }}
                >
                  <Box
                    sx={{
                      width: 56,
                      height: 32,
                      borderRadius: 1,
                      flexShrink: 0,
                      backgroundImage: ep.image ? `url(${ep.image})` : 'none',
                      backgroundSize: 'cover',
                      backgroundPosition: 'center',
                      backgroundColor: 'var(--anime-token-surface-variant)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 12,
                      fontWeight: 700,
                      color: 'var(--anime-token-text-secondary)',
                    }}
                  >
                    {!ep.image && ep.number}
                  </Box>
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography
                      variant="body2"
                      fontWeight={active ? 700 : 500}
                      noWrap
                      color={active ? 'var(--anime-token-brand-primary-main)' : 'var(--anime-token-text-primary)'}
                    >
                      Episode {ep.number}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                      {ep.title}
                    </Typography>
                  </Box>
                  {active && <PlayArrow sx={{ color: 'var(--anime-token-brand-primary-main)', fontSize: 18 }} />}
                </Box>
              </motion.div>
            );
          })}
        </Stack>
      )}
    </Box>
  );
}
